/**
 * 全局单例 TTS 播放器：同一时刻只一条，播新停旧。
 *
 * - 服务端：POST /api/tts → <audio>
 * - 503 tts_unavailable：静默退回 speechSynthesis，控制条只保留播放/暂停/关闭
 * - 分段流式代价：总时长播完前是估算；audio.duration 可能是 Infinity —— UI 绝不能显示 NaN/Infinity
 */

import { ensureFreshAccessToken } from "@/lib/api";
import { getSelectedVoiceId } from "@/lib/tts-voice";

export type TtsMode = "idle" | "server" | "system";
export type TtsStatus = "idle" | "loading" | "playing" | "paused";

export type TtsPlayerState = {
  mode: TtsMode;
  status: TtsStatus;
  currentTime: number;
  /** 用于 UI 的时长；永不 Infinity/NaN。null = 系统 TTS 不可用 */
  duration: number | null;
  /** true = 仍是估算/校准中（服务端分段流式） */
  durationIsEstimate: boolean;
  rate: number;
  text: string;
};

export const TTS_RATES = [0.75, 1, 1.25, 1.5, 2] as const;

const SEC_PER_CJK = 0.22;
const SEC_PER_LATIN = 0.07;

function estimateDuration(text: string): number {
  let cjk = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    if (ch >= 0x4e00 && ch <= 0x9fff) cjk++;
  }
  const other = Math.max(0, text.trim().length - cjk);
  return Math.max(0.3, cjk * SEC_PER_CJK + other * SEC_PER_LATIN);
}

/** UI 用：永不返回 Infinity / NaN */
export function formatTtsTime(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return "--:--";
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * 从 audio.duration 和估算值算出 UI 可用时长。
 * - Infinity / NaN / ≤0 → 用估算
 * - 播放中 current 超过估算 → 外推（边播边校准）
 */
export function resolveDisplayDuration(
  mediaDuration: number,
  estimate: number | null,
  currentTime: number
): { duration: number | null; isEstimate: boolean } {
  const mediaOk = Number.isFinite(mediaDuration) && mediaDuration > 0;
  if (mediaOk) {
    return { duration: mediaDuration, isEstimate: false };
  }
  if (estimate != null && Number.isFinite(estimate) && estimate > 0) {
    // 实际进度越过估算时，把终点推到 current + 缓冲，避免进度条卡死在 100%
    const grown = Math.max(estimate, currentTime + 1);
    return { duration: grown, isEstimate: true };
  }
  if (Number.isFinite(currentTime) && currentTime > 0) {
    return { duration: currentTime + 1, isEstimate: true };
  }
  return { duration: null, isEstimate: true };
}

type Listener = () => void;

class TtsPlayer {
  private listeners = new Set<Listener>();
  private state: TtsPlayerState = {
    mode: "idle",
    status: "idle",
    currentTime: 0,
    duration: null,
    durationIsEstimate: true,
    rate: 1,
    text: ""
  };

  private audio: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;
  private abort: AbortController | null = null;
  private estimate: number | null = null;
  private raf = 0;
  private generation = 0;
  private utterance: SpeechSynthesisUtterance | null = null;
  /** 待播队列。流式回答时按句入队，播完一句接下一句。 */
  private queue: string[] = [];

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getState = () => this.state;

  private emit() {
    this.listeners.forEach((l) => l());
  }

  private set(partial: Partial<TtsPlayerState>) {
    this.state = { ...this.state, ...partial };
    this.emit();
  }

  private clearMedia() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    if (this.audio) {
      this.audio.onplay = null;
      this.audio.onpause = null;
      this.audio.onended = null;
      this.audio.ontimeupdate = null;
      this.audio.onloadedmetadata = null;
      this.audio.onerror = null;
      this.audio.ondurationchange = null;
      try {
        this.audio.pause();
        this.audio.removeAttribute("src");
        this.audio.load();
      } catch {
        /* ignore */
      }
      this.audio = null;
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
    this.utterance = null;
  }

  private tickProgress = () => {
    const a = this.audio;
    if (!a || this.state.mode !== "server") return;
    const currentTime = Number.isFinite(a.currentTime) ? a.currentTime : 0;
    const { duration, isEstimate } = resolveDisplayDuration(a.duration, this.estimate, currentTime);
    this.set({
      currentTime,
      duration,
      durationIsEstimate: isEstimate,
      status: a.paused ? (this.state.status === "loading" ? "loading" : "paused") : "playing"
    });
    if (!a.paused) {
      this.raf = requestAnimationFrame(this.tickProgress);
    }
  };

  private startProgressLoop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(this.tickProgress);
  }

  /** 停掉一切并收起控制条 */
  stop = () => {
    this.queue = [];
    this.generation += 1;
    if (this.abort) {
      this.abort.abort();
      this.abort = null;
    }
    this.clearMedia();
    this.estimate = null;
    this.set({
      mode: "idle",
      status: "idle",
      currentTime: 0,
      duration: null,
      durationIsEstimate: true,
      text: ""
    });
  };

  toggle = () => {
    if (this.state.mode === "server" && this.audio) {
      if (this.audio.paused) {
        void this.audio.play().catch(() => undefined);
        this.set({ status: "playing" });
        this.startProgressLoop();
      } else {
        this.audio.pause();
        this.set({ status: "paused" });
      }
      return;
    }
    if (this.state.mode === "system") {
      if (typeof window === "undefined" || !window.speechSynthesis) return;
      // speechSynthesis 没有真正的 pause 在所有浏览器都可靠；尽量用 pause/resume
      if (this.state.status === "playing") {
        window.speechSynthesis.pause();
        this.set({ status: "paused" });
      } else if (this.state.status === "paused") {
        window.speechSynthesis.resume();
        this.set({ status: "playing" });
      }
    }
  };

  seek = (seconds: number) => {
    if (this.state.mode !== "server" || !this.audio) return;
    const a = this.audio;
    const { duration } = resolveDisplayDuration(a.duration, this.estimate, a.currentTime);
    const max = duration != null && Number.isFinite(duration) ? duration : a.currentTime + 60;
    const next = Math.max(0, Math.min(seconds, max));
    try {
      a.currentTime = next;
    } catch {
      return;
    }
    this.set({ currentTime: next });
  };

  skip = (delta: number) => {
    if (this.state.mode !== "server" || !this.audio) return;
    this.seek(this.audio.currentTime + delta);
  };

  setRate = (rate: number) => {
    const r = TTS_RATES.includes(rate as (typeof TTS_RATES)[number]) ? rate : 1;
    this.set({ rate: r });
    if (this.audio) this.audio.playbackRate = r;
  };

  /**
   * @param options.voice 覆盖当前设置的音色（设置页试听用）；不传则读 localStorage
   */
  speak = (text: string, options?: { voice?: string | null }) => {
    // 显式朗读 = 从头开始，把排队的句子丢掉
    this.queue = [];
    this.speakOne(text, options);
  };

  /** 排队朗读：流式回答用——第一句先播，后面的句子到了排在它后面。
   *
   *  和 speak 的区别只在"要不要打断当前这句"：speak 是"改播这个"，
   *  enqueue 是"接着播这个"。空闲时两者等价。 */
  enqueue = (text: string, options?: { voice?: string | null }) => {
    const cleaned = text.replace(/\s+/g, " ").trim();
    if (!cleaned) return;
    if (this.state.mode === "idle") {
      this.speakOne(cleaned, options);
      return;
    }
    this.queue.push(cleaned);
  };

  /** 当前这句放完：有下一句就接上，没有才真正停。 */
  private advanceOrStop() {
    const next = this.queue.shift();
    if (next === undefined) {
      this.stop();
      return;
    }
    this.speakOne(next);
  }

  private speakOne = (text: string, options?: { voice?: string | null }) => {
    const cleaned = text.replace(/\s+/g, " ").trim();
    if (!cleaned) return;

    // 播新停旧
    this.generation += 1;
    const gen = this.generation;
    if (this.abort) {
      this.abort.abort();
      this.abort = null;
    }
    this.clearMedia();
    this.estimate = estimateDuration(cleaned);

    this.set({
      mode: "server",
      status: "loading",
      currentTime: 0,
      duration: this.estimate,
      durationIsEstimate: true,
      text: cleaned
    });

    void this.playServer(cleaned, gen, options?.voice);
  };

  private async playServer(text: string, gen: number, voiceOverride?: string | null) {
    const ac = new AbortController();
    this.abort = ac;

    try {
      const token = await ensureFreshAccessToken();
      if (gen !== this.generation) return;
      if (!token) {
        // 未登录：静默系统 TTS
        this.playSystem(text, gen);
        return;
      }

      // voiceOverride === undefined → 用设置；null / 具体 id → 按传入值
      const voice = voiceOverride !== undefined ? voiceOverride : getSelectedVoiceId();
      const res = await fetch("/api/tts", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          text,
          ...(voice ? { voice } : {}),
          speed: 1.0
        }),
        signal: ac.signal
      });

      if (gen !== this.generation) return;

      if (res.status === 503) {
        // 必须静默降级，不要 toast
        let isUnavailable = true;
        try {
          const j = (await res.json()) as { error?: string };
          isUnavailable = j.error === "tts_unavailable" || !j.error;
        } catch {
          isUnavailable = true;
        }
        if (isUnavailable) {
          this.playSystem(text, gen);
          return;
        }
        this.playSystem(text, gen);
        return;
      }

      if (!res.ok) {
        // 其它错误同样静默退回系统 TTS——用户只是想听朗读
        this.playSystem(text, gen);
        return;
      }

      const headerDur = parseFloat(res.headers.get("X-Audio-Duration") || "");
      if (Number.isFinite(headerDur) && headerDur > 0) {
        this.estimate = headerDur;
        this.set({ duration: headerDur, durationIsEstimate: true });
      }

      // 边收边播。服务端本来就是分段 chunked 返回的，但这里原先写的是
      // `await res.blob()`——等整个响应下载完才开始播，把流式白白浪费掉。
      // fire→ECS 的隧道只有 2~9 KB/s，整段音频动辄几百 KB，等下载完就是
      // 几十秒的干等。改成 MediaSource：第一段到了就出声，剩下的边播边收。
      const mse = await this.playViaMediaSource(res, gen);
      if (mse) return;

      // MSE 不可用（Safari 老版本 / 不支持 audio/mpeg）时退回整包下载
      const blob = await res.blob();
      if (gen !== this.generation) return;
      if (!blob.size) {
        this.playSystem(text, gen);
        return;
      }

      const url = URL.createObjectURL(blob);
      this.objectUrl = url;
      const audio = new Audio();
      audio.preload = "auto";
      audio.playbackRate = this.state.rate;
      this.audio = audio;

      audio.onloadedmetadata = () => {
        if (gen !== this.generation) return;
        const { duration, isEstimate } = resolveDisplayDuration(
          audio.duration,
          this.estimate,
          audio.currentTime
        );
        this.set({ duration, durationIsEstimate: isEstimate });
      };
      audio.ondurationchange = () => {
        if (gen !== this.generation) return;
        const { duration, isEstimate } = resolveDisplayDuration(
          audio.duration,
          this.estimate,
          audio.currentTime
        );
        this.set({ duration, durationIsEstimate: isEstimate });
      };
      audio.onplay = () => {
        if (gen !== this.generation) return;
        this.set({ status: "playing" });
        this.startProgressLoop();
      };
      audio.onpause = () => {
        if (gen !== this.generation) return;
        if (audio.ended) return;
        this.set({ status: "paused" });
      };
      audio.onended = () => {
        if (gen !== this.generation) return;
        // Safari 等不支持 MSE 的浏览器走这里。以前第一段播完直接 stop()，
        // 会把后续排队段落全部清空，表现就是“只读了开头”。
        this.advanceOrStop();
      };
      audio.onerror = () => {
        if (gen !== this.generation) return;
        this.playSystem(text, gen);
      };

      audio.src = url;
      try {
        await audio.play();
      } catch {
        if (gen !== this.generation) return;
        // 自动播放被拦时仍展示控制条，用户点播放
        this.set({ status: "paused", mode: "server" });
      }
    } catch (e) {
      if (gen !== this.generation) return;
      if (e instanceof DOMException && e.name === "AbortError") return;
      this.playSystem(text, gen);
    } finally {
      if (this.abort === ac) this.abort = null;
    }
  }

  /** 用 MediaSource 边收边播。成功返回 true；不支持或中途失败返回 false 由调用方兜底。 */
  private async playViaMediaSource(res: Response, gen: number): Promise<boolean> {
    if (typeof MediaSource === "undefined" || !res.body) return false;
    const mime = (res.headers.get("content-type") || "").split(";")[0].trim();
    // 只对 mp3 走 MSE：WAV 的头带总长度，未知总长时没法正确拼流
    if (mime !== "audio/mpeg" || !MediaSource.isTypeSupported(mime)) return false;

    const reader = res.body.getReader();
    const first = await reader.read();
    if (gen !== this.generation) {
      void reader.cancel();
      return true; // 已被新的朗读取代，不该再走兜底
    }
    if (first.done || !first.value?.length) return false;

    const ms = new MediaSource();
    const url = URL.createObjectURL(ms);
    this.objectUrl = url;
    const audio = new Audio();
    audio.preload = "auto";
    audio.playbackRate = this.state.rate;
    this.audio = audio;
    this.attachAudioHandlers(audio, gen);
    audio.src = url;

    await new Promise<void>((resolve) => {
      ms.addEventListener("sourceopen", () => resolve(), { once: true });
    });
    if (gen !== this.generation) return true;

    let sb: SourceBuffer;
    try {
      sb = ms.addSourceBuffer(mime);
    } catch {
      return false;
    }

    const queue: Uint8Array[] = [first.value];
    let done = false;
    const pump = () => {
      if (sb.updating) return;
      const next = queue.shift();
      if (next) {
        try {
          // 复制成独立 ArrayBuffer：reader 给的 Uint8Array 可能挂在 SharedArrayBuffer 上，
          // TS 的 BufferSource 不接受，运行时也可能被后续读取复用底层内存
          sb.appendBuffer(next.slice().buffer as ArrayBuffer);
        } catch {
          // 缓冲区满等异常：丢弃这块，继续播已有的
        }
      } else if (done && ms.readyState === "open") {
        try {
          ms.endOfStream();
        } catch {
          /* 已经结束 */
        }
      }
    };
    sb.addEventListener("updateend", pump);
    pump();

    void (async () => {
      try {
        for (;;) {
          const { done: d, value } = await reader.read();
          if (gen !== this.generation) {
            void reader.cancel();
            return;
          }
          if (d) break;
          if (value?.length) {
            queue.push(value);
            pump();
          }
        }
      } catch {
        // 传输中断：让已收到的部分播完
      } finally {
        done = true;
        pump();
      }
    })();

    try {
      await audio.play();
    } catch {
      if (gen === this.generation) this.set({ status: "paused", mode: "server" });
    }
    return true;
  }

  /** 播放器事件绑定。MSE 和整包下载两条路径共用，避免两处漂移。 */
  private attachAudioHandlers(audio: HTMLAudioElement, gen: number) {
    audio.onloadedmetadata = () => {
      if (gen !== this.generation) return;
      const { duration, isEstimate } = resolveDisplayDuration(audio.duration, this.estimate, audio.currentTime);
      this.set({ duration, durationIsEstimate: isEstimate });
    };
    audio.ondurationchange = () => {
      if (gen !== this.generation) return;
      const { duration, isEstimate } = resolveDisplayDuration(audio.duration, this.estimate, audio.currentTime);
      this.set({ duration, durationIsEstimate: isEstimate });
    };
    audio.onplay = () => {
      if (gen !== this.generation) return;
      this.set({ status: "playing" });
      this.startProgressLoop();
    };
    audio.onpause = () => {
      if (gen !== this.generation) return;
      if (audio.ended) return;
      this.set({ status: "paused" });
    };
    audio.onended = () => {
      if (gen !== this.generation) return;
      this.advanceOrStop();
    };
    audio.onerror = () => {
      if (gen !== this.generation) return;
      this.playSystem(this.state.text, gen);
    };
  }

  private playSystem(text: string, gen: number) {
    if (gen !== this.generation) return;
    this.clearMedia();
    this.estimate = null;

    if (typeof window === "undefined" || !window.speechSynthesis) {
      this.set({ mode: "idle", status: "idle", text: "", duration: null, currentTime: 0 });
      return;
    }

    const u = new SpeechSynthesisUtterance(text);
    u.lang = "zh-CN";
    u.rate = this.state.rate >= 1.5 ? 1.2 : this.state.rate <= 0.75 ? 0.85 : 1;
    this.utterance = u;

    u.onstart = () => {
      if (gen !== this.generation) return;
      this.set({ mode: "system", status: "playing", duration: null, durationIsEstimate: true, currentTime: 0 });
    };
    u.onend = () => {
      if (gen !== this.generation) return;
      this.advanceOrStop();
    };
    u.onerror = () => {
      if (gen !== this.generation) return;
      this.stop();
    };

    this.set({
      mode: "system",
      status: "loading",
      duration: null,
      durationIsEstimate: true,
      currentTime: 0,
      text
    });

    try {
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
      // 部分浏览器 onstart 会晚/丢，保险置为 playing
      this.set({ mode: "system", status: "playing" });
    } catch {
      this.stop();
    }
  }
}

export const ttsPlayer = new TtsPlayer();

/** 单次送给 /api/tts 的上限。服务端是 5000 字符直接 400，留些余量。
 *  超了不能硬送——400 会让 playServer 静默退回浏览器 speechSynthesis，
 *  而 Chrome 对长文本有已知的「读到一半无声中断」问题，不报错也不触发
 *  onerror，用户看到的就是「读着读着没声了」（2026-08-18）。 */
// 分得更稳一些：服务端虽允许 5000 字，但长音频经过慢隧道时更容易中断；
// 1800 字既能保持语气连续，也让失败重试/系统 TTS 兜底的单段足够短。
const TTS_CHUNK_LIMIT = 1800;

/** 长文本按句切成不超过上限的几段。优先在句末标点断，其次换行，最后硬切。 */
function splitForTts(text: string): string[] {
  if (text.length <= TTS_CHUNK_LIMIT) return [text];
  const out: string[] = [];
  let rest = text;
  while (rest.length > TTS_CHUNK_LIMIT) {
    const window = rest.slice(0, TTS_CHUNK_LIMIT);
    let cut = Math.max(
      window.lastIndexOf("。"), window.lastIndexOf("！"), window.lastIndexOf("？"),
      window.lastIndexOf("."), window.lastIndexOf("!"), window.lastIndexOf("?")
    );
    if (cut < TTS_CHUNK_LIMIT * 0.5) cut = window.lastIndexOf("\n");
    if (cut < TTS_CHUNK_LIMIT * 0.5) cut = TTS_CHUNK_LIMIT - 1;
    out.push(rest.slice(0, cut + 1).trim());
    rest = rest.slice(cut + 1);
  }
  if (rest.trim()) out.push(rest.trim());
  return out.filter(Boolean);
}

/** React 订阅：在组件里 useSyncExternalStore(ttsPlayer.subscribe, ttsPlayer.getState, ttsPlayer.getState) */
export function speakText(text: string, options?: { voice?: string | null }) {
  const parts = splitForTts(text);
  ttsPlayer.speak(parts[0], options);
  // 后面几段排队接着念，不再走会哑掉的浏览器兜底
  for (const p of parts.slice(1)) ttsPlayer.enqueue(p, options);
}

/** 排队朗读（流式回答按句喂进来）。见 TtsPlayer.enqueue。 */
export function speakTextQueued(text: string, options?: { voice?: string | null }) {
  // 流式结束时补上的“尾巴”也可能很长。原来这里不分段，超过服务端上限后
  // 会降级到浏览器长文本朗读，而 Chrome 常在中途静默停止。
  for (const part of splitForTts(text)) ttsPlayer.enqueue(part, options);
}

/** 独立试听：不走全局播放器，所以不会把朗读控制条弹出来。
 *
 *  试听是"在设置面板里确认这个音色好不好听"，不是"开始朗读一段内容"。
 *  走 ttsPlayer.speak 的话控制条会顶在面板后面，多出来一个框很碍眼。 */
let previewAudio: HTMLAudioElement | null = null;
let previewAbort: AbortController | null = null;

export function stopVoicePreview() {
  previewAbort?.abort();
  previewAbort = null;
  if (previewAudio) {
    previewAudio.pause();
    if (previewAudio.src.startsWith("blob:")) URL.revokeObjectURL(previewAudio.src);
    previewAudio = null;
  }
}

export async function previewVoice(text: string, voice: string): Promise<void> {
  stopVoicePreview();
  const ac = new AbortController();
  previewAbort = ac;
  const token = await ensureFreshAccessToken();
  if (!token || ac.signal.aborted) return;
  const res = await fetch("/api/tts", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ text, voice, speed: 1.0 }),
    signal: ac.signal
  });
  if (!res.ok || ac.signal.aborted) return;
  const blob = await res.blob();
  if (!blob.size || ac.signal.aborted) return;
  const audio = new Audio(URL.createObjectURL(blob));
  previewAudio = audio;
  audio.onended = () => stopVoicePreview();
  try {
    await audio.play();
  } catch {
    /* 自动播放被拦：静默，用户会再点一次 */
  }
}
